import { useTranslation } from 'react-i18next'
import { useCurrentEntitlement } from '@/hooks/admin/useContentFee'

function formatDate(iso: string | null, locale: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(locale)
}

/**
 * 원생 상세 enrollment 탭 — 현재 콘텐츠 사용료 수강권 요약(gray panel).
 * 로딩은 숨김, null/에러는 "수강권 없음" 힌트로 fail-soft.
 */
export function ContentFeeEntitlementPanel({ userId }: { userId: number }) {
  const { t, i18n } = useTranslation(['admin'])
  const { data, isLoading, isError } = useCurrentEntitlement(userId)
  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'

  if (isLoading) return null

  if (isError || !data) {
    return (
      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500">
        {t('admin:students.detail.contentFee.noEntitlement')}
      </div>
    )
  }

  const periodStart = formatDate(data.periodStart, locale)
  const periodEnd = formatDate(data.periodEnd, locale)
  const graceUntil = formatDate(data.graceUntil, locale)
  const periodLabel = periodStart
    ? `${periodStart} ~ ${periodEnd ?? ''}`
    : t('admin:students.detail.contentFee.fields.notStarted')

  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{t('admin:students.detail.contentFee.title')}</span>
        <span className="text-xs text-gray-500">{data.status}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.level')}</span>
        <span className="text-gray-800">{data.level}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.contentTitle')}</span>
        <span className="text-gray-800">{data.contentTitle ?? '—'}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.cumulativeGranted')}</span>
        <span className="text-gray-800">{data.cumulativeGrantedLessons}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.cumulativeUsed')}</span>
        <span className="text-gray-800">{data.cumulativeUsedLessons}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.remaining')}</span>
        <span className="text-gray-800">{data.remaining}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.currentGranted')}</span>
        <span className="text-gray-800">{data.grantedLessons}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.carriedOver')}</span>
        <span className="text-gray-800">{data.carriedOver}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.period')}</span>
        <span className="text-gray-800">{periodLabel}</span>
      </div>
      {graceUntil && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{t('admin:students.detail.contentFee.fields.graceUntil')}</span>
          <span className="text-gray-800">{graceUntil}</span>
        </div>
      )}
    </div>
  )
}
